import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  DialogContent,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  TopSection,
  ProfileImageWrapper,
  ProfileImageInnerWrapper,
  TextWrapper,
  Divider,
  ToolTipStyled,
} from "./styles";
import TextView from "../../../../TextView";
import SearchField from "../../ListComponents/SearchField";
import { tokens, colors } from "../../../../../utils";
import ReuseableButton from "../../../Button";
import { addFolder, updateFolder } from "../../../../../redux/reducers/gallery";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { createSubmitGuard } from "../../../../../utils/submitGuard";
// import { v4 as uuidv4 } from "uuid";
import {
  useCreateFolder,
  useUpdateFolder,
} from "../../../../../services/mutations";
// kelly-0916-collections-edit-update-slow-spinner: the save-in-flight state is
// an INLINE brand loader beside the buttons, never the full-screen
// BackdropSpinner. That Backdrop sat at z-index 10000 OVER this dialog, so
// Cancel and X were dead for the whole (slow, measured multi-second) PATCH.
import BrandWheelLoader from "../../../../BrandWheelLoader";
import CustomTooltip from "../../../../SegmentBuilder/Shared/CustomTooltip";
import CustomSwitch from "../../../../CustomSwitch";
import Tooltip from "@mui/material/Tooltip";
// Gordon 2026-08-27 change 1: reuse the SAME Public/Private/Hidden picker (and
// the SAME wire values -- 1/2/3, matching Wheel.visibility) a Wheel uses, so a
// Collection's visibility never drifts into its own bespoke model again.
import WheelVisibilityOptions from "../../../../WheelVisibilityOptions";
import { WHEEL_VISIBILITY } from "../../../../../constants/wheelVisibility";
// #920 §1: the create/edit dialog is user-facing gallery UI, so its wording
// comes from the shared Collections vocabulary. The folder MODEL and every
// payload key below (`name`, `visibility`, `gallery_visibility`) are unchanged.
import {
  COLLECTION_DIALOG_COPY,
  COLLECTION_TITLE_MAX_LENGTH,
  COLLECTION_DESCRIPTION_MAX_LENGTH,
} from "../../FoldersV2/collectionsCopy";

export let CreateFolderDialogProps = {};
const StyledDialog = styled(Dialog)(({ theme }) => ({
  ".MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  ".MuiPaper-root": {
    border: "1px solid #E1E1E1",
    borderRadius: 8,
    width: "40vw",
    // stgqa-0927-P0-kelly-gate-e-phone-browser-390-full-path: 40vw of a 390px
    // phone viewport is 156px -- measured live, every label/option in this
    // dialog ("Description (optional)", "Hide Wheels from Gallery", "Public/
    // Private/Hidden") wrapped character-by-character down a column that
    // narrow. Widen to fit the phone viewport below the sm breakpoint.
    [theme.breakpoints.down("sm")]: {
      width: "calc(100vw - 32px)",
    },
    // height: "68vh",
    // stgfix-0927-P0-kelly-gate-b-gallery-collections: 40vw with no breakpoint
    // override renders a 156px-wide dialog on a 390px mobile viewport (MEASURED
    // live on web-stg.iyuga.net) -- the "Hide Wheels from Gallery" toggle label
    // wraps character-by-character and the Create/Update button lands at the
    // very bottom edge of the viewport, often below the fold. Same breakpoint
    // shape as the sibling ProfilePopupV2.jsx dialog in this same area.
    [theme.breakpoints.down("lg")]: { width: "55vw" },
    [theme.breakpoints.down("md")]: { width: "78vw" },
    [theme.breakpoints.down("sm")]: {
      width: "calc(100% - 32px)",
      maxWidth: "calc(100% - 32px)",
      margin: 16,
    },
  },
}));

const CreateFolderDialog = ({ onCancel = () => {} }) => {
  const [open, setOpen] = useState(false);
  const [folderTitle, setFolderTitle] = useState("");
  // BUG-953: shown in the collection's Share preview (og:description).
  const [folderDescription, setFolderDescription] = useState("");
  const [folderId, setFolderId] = useState(null);
  const createFolder = useCreateFolder();
  const _updateFolder = useUpdateFolder();
  const [parentId, setParentId] = useState(null);
  const [loader, setLoader] = useState(false);
  const [exclude, setExclude] = useState(false);
  // gordon-0917-collections-hidden-badge-repeat-breaker: an EDIT save must only
  // write `gallery_visibility` when the owner actually touched the "Hide
  // Contents from Gallery" switch THIS session -- otherwise a rename/visibility
  // -only save resends whatever `exclude` was last seeded with, which is safe
  // today only because `setFolderFormData` always seeds it correctly. Tracking
  // "touched" separately means a future seeding bug can never silently
  // overwrite a collection's real `gallery_visibility` on an unrelated edit
  // (matches the same guarded-field pattern the backend already uses for
  // `description`/`cover_wheel_id` -- see partial_update in folder/base.py).
  const [excludeTouched, setExcludeTouched] = useState(false);
  const [visibility, setVisibility] = useState(WHEEL_VISIBILITY.PUBLIC);

  // gordon-0921-WEB-collection-kebab-...-update-button-dirty-gate: a snapshot
  // of the values EDIT mode was seeded with (set once, in `setFolderFormData`,
  // never touched by subsequent typing) so `isDirty` below can compare
  // current-vs-initial without a second piece of form-library state. null in
  // CREATE mode -- the Update-button gate only ever applies while `folderId`
  // is set, so a null snapshot is never read.
  const initialValuesRef = useRef(null);

  // Synchronous re-entrancy guard for handleCreate's async mutate() calls --
  // same pattern (and the same measured login double-submit defect) as
  // src/pages/Auth/Login/index.jsx; see src/utils/submitGuard.js.
  const submitInFlight = useRef(false);
  const submitGuard = createSubmitGuard(submitInFlight);

  const dispatch = useDispatch();
  const handleCancel = () => {
    setOpen(false);
    resetState();
    onCancel && onCancel();
  };

  const formOpen = () => setOpen(true);
  const formClose = () => {
    setOpen(false);
    resetState();
  };
  const resetState = () => {
    setFolderId(null);
    setFolderTitle("");
    setFolderDescription("");
    setExclude(false);
    setExcludeTouched(false);
    setVisibility(WHEEL_VISIBILITY.PUBLIC);
    initialValuesRef.current = null;
  };

  const handleParentId = (id) => {
    setParentId(id);
  };
  const setFolderFormData = (data = {}) => {
    const {
      title,
      description,
      visibility: folderVisibility,
      id,
      gallery_visibility,
    } = data || {};
    const seededTitle = title || "";
    const seededDescription = description || "";
    const seededExclude = !!gallery_visibility;
    const seededVisibility = folderVisibility || WHEEL_VISIBILITY.PUBLIC;
    setFolderTitle(seededTitle);
    setFolderDescription(seededDescription);
    setExclude(seededExclude);
    setExcludeTouched(false);
    setVisibility(seededVisibility);
    setFolderId(id);
    initialValuesRef.current = {
      title: seededTitle.trim(),
      description: seededDescription,
      exclude: seededExclude,
      visibility: seededVisibility,
    };
  };

  useEffect(() => {
    CreateFolderDialogProps = {
      formOpen,
      formClose,
      handleCancel,
      setFolderFormData,
      handleParentId,
      resetState,
    };
    return () => {
      CreateFolderDialogProps = {};
      setFolderId(null);
      setExclude(false);
    };
  }, []);

  // Reusing WheelVisibilityOptions' native RadioGroup contract: the handler
  // receives a real change event, not a bare type string.
  const handleVisibilityChange = (event) => {
    setVisibility(Number(event.target.value));
  };

  const resetForm = () => {
    setFolderId(null);
    setFolderTitle("");
    setFolderDescription("");
    setExclude(false);
    setExcludeTouched(false);
    setVisibility(WHEEL_VISIBILITY.PUBLIC);
    initialValuesRef.current = null;
  };

  // Gordon 2026-09-21 (second note, Edit Collection dialog screenshot):
  // "Update button should be grayed out if the user didn't make any change."
  // One isDirty, current-vs-the-snapshot `setFolderFormData` took at open --
  // no second/hand-rolled dirty tracker.
  const isDirty =
    !initialValuesRef.current ||
    folderTitle.trim() !== initialValuesRef.current.title ||
    folderDescription !== initialValuesRef.current.description ||
    exclude !== initialValuesRef.current.exclude ||
    visibility !== initialValuesRef.current.visibility;

  // Only EDIT mode (folderId set) is gated -- Create keeps its own pre-existing
  // rule (needs a name, enforced by the toast in handleCreate below).
  const isEditing = Boolean(folderId);
  const updateDisabled =
    isEditing && (!isDirty || !folderTitle.trim() || loader);

  const handleCreate = () => {
    if (!folderTitle)
      return toast.error(COLLECTION_DIALOG_COPY.titleRequiredError);
    if (!submitGuard.tryAcquire()) return;

    setLoader(true);
    if (!folderId) {
      let _payload = {
        name: folderTitle,
        description: folderDescription,
        visibility,
        parent: parentId,
        tags: [{ name: "Important" }],
        gallery_visibility: exclude,
      };
      createFolder.mutate(_payload, {
        onSuccess: (data) => {
          toast.success(COLLECTION_DIALOG_COPY.createdToast);
          const payload = {
            ..._payload,
            title: folderTitle,
            description: folderDescription,
            parentId,
            id: data?.data?.data?.id,
            isPrimary: false,
            type: "folder",
            items: [],
            date: data?.data?.data?.updated_at,
            size: 0,
            content_amount: 0,
            gallery_visibility: exclude,
            tags: _payload?.tags?.map((tag) => tag.name),
          };
          dispatch(addFolder(payload));
          setParentId(null);
          resetForm();
          setOpen(false);
        },
        onError: (error) => {
          console.log(error);
          toast.error("Something went wrong");
        },
        onSettled: () => {
          setLoader(false);
          submitGuard.release();
        },
      });
    } else {
      let _payload = {
        id: folderId,
        name: folderTitle,
        description: folderDescription,
        visibility,
        // gordon-0917-collections-hidden-badge-repeat-breaker: only send
        // `gallery_visibility` when the owner actually flipped the switch this
        // session. Omitting it on every other save (rename, visibility-only,
        // etc.) lets the backend's `if gallery_visibility is not None` guard
        // leave the stored value untouched, instead of this dialog re-sending
        // whatever `exclude` happened to be seeded with.
        ...(excludeTouched ? { gallery_visibility: exclude } : {}),
      };
      _updateFolder.mutate(_payload, {
        onSuccess: () => {
          dispatch(
            updateFolder({
              id: folderId,
              updates: {
                title: folderTitle,
                description: folderDescription,
                visibility,
                ...(excludeTouched ? { gallery_visibility: exclude } : {}),
              },
            })
          );
          resetForm();
          setOpen(false);
        },
        onError: (error) => {
          console.log({ updateError: error });
          toast.error("Something went wrong");
        },
        onSettled: () => {
          setLoader(false);
          submitGuard.release();
        },
      });
    }

    // const payload = {
    //   id: uuidv4(),
    //   title: folderTitle,
    //   visibility: getPrimaryStatus(),
    //   isPrimary: false,
    //   isFavorite: false,
    //   type: "folder",
    //   items: [],
    //   parentId: null,
    //   date: new Date().toISOString(),
    //   size: 0,
    //   content_amount: 0,
    //   tags: ["food", "nature", "people"],
    // };

    // folderId
    //   ? dispatch(
    //       updateFolder({
    //         id: folderId,
    //         updates: {
    //           title: folderTitle,
    //           visibility,
    //         },
    //       })
    //     )
    //   : dispatch(addFolder(payload));
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={formClose}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <DialogTitle
          sx={{
            padding: "5px 10px",
          }}
        >
          <TopSection>
            <ProfileImageWrapper>
              <ProfileImageInnerWrapper>
                {/* Gordon 2026-08-27 change 4: the Create Collection header no
                    longer carries a folder+ icon -- only Edit keeps its icon,
                    since only Create was called out. */}
                {folderId && (
                  <div style={{ paddingTop: 7 }}>
                    <EditIcon
                      style={{
                        fontSize: 28,
                        color: "black",
                      }}
                    />
                  </div>
                )}
                <TextWrapper>
                  <TextView variant="title">
                    {folderId
                      ? COLLECTION_DIALOG_COPY.editTitle
                      : COLLECTION_DIALOG_COPY.createTitle}
                  </TextView>
                </TextWrapper>
              </ProfileImageInnerWrapper>
            </ProfileImageWrapper>
            <IconButton aria-label="close" onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </TopSection>
        </DialogTitle>
        <Divider />

        <DialogContent>
          <SearchField
            height={48}
            placeholder={COLLECTION_DIALOG_COPY.titleFieldPlaceholder}
            isHideIcon={true}
            // QA-1261 — cap the Collection name so it can't be created unbounded.
            maxLength={COLLECTION_TITLE_MAX_LENGTH}
            value={folderTitle}
            onChange={(val) => setFolderTitle(val)}
            // Without this the shared SearchField's default leaks through and a
            // screen reader announces this field as "Search wheels" (measured).
            ariaLabel={COLLECTION_DIALOG_COPY.titleFieldAriaLabel}
          />

          {/* BUG-953: shown in the Share preview's og:description. */}
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            variant="outlined"
            placeholder={COLLECTION_DIALOG_COPY.descriptionFieldPlaceholder}
            aria-label={COLLECTION_DIALOG_COPY.descriptionFieldAriaLabel}
            value={folderDescription}
            onChange={(e) =>
              setFolderDescription(
                e.target.value.slice(0, COLLECTION_DESCRIPTION_MAX_LENGTH)
              )
            }
            sx={{
              marginTop: "10px",
              // ISP-9999 (gordon-0923-P1-REPEAT-kelly-field-focus-style-one-
              // shared-border-only-app-wide) — supersedes the
              // slack-dev-0902 fix above (which pinned the notchedOutline to
              // its resting grey and relied on the app-wide outline+box-
              // shadow ring painted on the inner input instead). That was
              // itself one of the inconsistent per-field focus treatments
              // Kelly kept reporting. The ONE effect now is the shared
              // `dataFieldFocus` token: the notchedOutline border recolors
              // on focus, nothing else — matching the Edit Wheel "Wheel
              // Name" Froala reference field (src/theme/tokens.js).
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0, 0, 0, 0.23)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0, 0, 0, 0.23)",
              },
              ...tokens.dataFieldFocus.muiSx,
            }}
          />

          {/* Start Exclude toggle */}
          <div style={{ display: "flex", marginTop: 10 }}>
            <CustomSwitch
              checked={exclude}
              onChange={() => {
                setExclude(!exclude);
                setExcludeTouched(true);
              }}
              inputProps={{ "aria-label": "controlled" }}
            />
            <TextView
              variant="text"
              style={{
                marginTop: 7,
              }}
            >
              {COLLECTION_DIALOG_COPY.hideContentsLabel}
            </TextView>
            <Tooltip title={COLLECTION_DIALOG_COPY.hideContentsTooltip}>
              <ToolTipStyled>i</ToolTipStyled>
            </Tooltip>
          </div>
          {/* End Exclude toggle */}

          <WheelVisibilityOptions
            name="collection-visibility"
            value={visibility}
            onChange={handleVisibilityChange}
            label="Visibility to:"
          />
          <Divider />
          <DialogActions>
            <div
              style={{ marginTop: 10, display: "flex", alignItems: "center" }}
            >
              {loader && (
                <BrandWheelLoader
                  variant="inline"
                  size={22}
                  label={folderId ? "Saving…" : "Creating…"}
                  data-testid="collection-dialog-saving"
                />
              )}
              <ReuseableButton
                onClick={handleCancel}
                text={"Cancel"}
                fullWidth={false}
                variant="text"
                textColor="black"
                customStyles={{
                  padding: "4px 20px",
                }}
              />

              <ReuseableButton
                text={folderId ? "Update" : "Create"}
                fullWidth={false}
                disabled={updateDisabled}
                aria-disabled={updateDisabled}
                customStyles={{
                  // DL-004 reach (ISP-4018): white-text fill -> on-fill token.
                  backgroundColor: updateDisabled
                    ? colors.disabled
                    : tokens.brand.primaryActive,
                  // gordon-0921 Update-dirty-gate: the app's disabled button
                  // token (same colors.disabled/colors.mediumGrey pair
                  // HelpPanel.jsx's send IconButton uses), not a bespoke color.
                  "&.Mui-disabled": {
                    backgroundColor: colors.disabled,
                    color: colors.mediumGrey,
                  },
                  padding: "4px 20px",
                }}
                textColor={updateDisabled ? colors.mediumGrey : undefined}
                onClick={handleCreate}
              />
            </div>
          </DialogActions>
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default CreateFolderDialog;
